import { useEffect, useState } from 'react'
import {
  api,
  type PageHealth,
  type PulseSummary,
  type ReaderFeedbackPage,
  type SearchQuestionStat,
  type State,
} from '../api'
import { parseSearchTuning, serializeSearchTuning } from '@staleless/schema'
import { ago, titleOf } from '../pulse-format'
import { Icon } from './Icon'

/**
 * Pulse, as a screen — not just a report `scripts/pulse.ts` prints to a
 * terminal.
 *
 * The health model is the product's own name: BRAND.md's three states, each
 * carrying a SHAPE as well as a colour so the row reads in greyscale and for a
 * colour-blind reader. `/api/health` runs the same assessment `/api/scan` does,
 * without writing anything to the shared inbox — reading how healthy the
 * documentation is must not change what anybody else sees there.
 */
const MARK: Record<PageHealth['state'], string> = { current: '●', review: '◐', outdated: '○' }
const LABEL: Record<PageHealth['state'], string> = {
  current: 'Current',
  review: 'Needs review',
  outdated: 'Outdated',
}

export function PulseScreen({
  state,
  branch,
  onOpenPage,
  onWrite,
  onAnalytics,
}: {
  state: State
  /** Opens the page in the editor. The reason any of this leads anywhere. */
  /** The draft being worked on, or null on the live site. */
  branch: string | null
  onOpenPage: (path: string) => void
  /**
   * The door out of every empty state on this screen: Pages.
   *
   * Pulse reports on written, published pages, so each of its empty states has
   * the same honest answer to "what do I do?" — go and write one. Saying that
   * without a way to act on it is what an empty screen with no button is.
   */
  onWrite: () => void
  /**
   * The whole reading picture, which is a screen of its own.
   *
   * Pulse says whether the documentation is still TRUE; Analytics says whether
   * anybody is reading it, and the two are read together. Home used to carry a
   * "Readers" card whose only real job was this door, and retiring that card
   * took the door with it — leaving Analytics reachable from exactly one place,
   * three clicks down inside Settings, which is where a screen goes to be
   * forgotten. This is the door back, on the screen its numbers belong beside.
   */
  onAnalytics: () => void
}) {
  const [health, setHealth] = useState<{
    summary: PulseSummary
    pages: PageHealth[]
    withoutDates: boolean
  } | null>(null)
  const [error, setError] = useState<string | null>(null)

  /**
   * Change one row's quiet state without refetching the whole assessment.
   *
   * The assessment is a git read across every page; asking for it again to
   * change one date on one row would make a small button feel like a page
   * load. The answer names the new state, so this writes exactly what the
   * server said rather than guessing at it.
   */
  const setQuiet = (path: string, snoozedUntil: string | null) =>
    setHealth((was) =>
      was === null
        ? was
        : { ...was, pages: was.pages.map((p) => (p.path === path ? { ...p, snoozedUntil } : p)) },
    )
  const snooze = async (path: string, days: number) => {
    try {
      const answer = await api.snoozeReview(path, days)
      setQuiet(path, answer.snoozedUntil)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'That could not be snoozed.')
    }
  }
  const wake = async (path: string) => {
    try {
      await api.wakeReview(path)
      setQuiet(path, null)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'That could not be changed.')
    }
  }
  /** What readers said. Null while loading; 'unavailable' rather than a throw. */
  const [readers, setReaders] = useState<ReaderFeedbackPage[] | 'unavailable' | null>(null)
  /** What readers searched for. Same contract as `readers`. */
  /**
   * Grouped by question, not by spelling.
   *
   * The raw log is still sent and is still the truth about what was typed;
   * this screen is where somebody decides what to write, and four rows of one
   * search each — "export", "how do I export", "exports", "exporting" — read
   * as four people with a passing interest instead of one page that is
   * missing.
   */
  const [searches, setSearches] = useState<SearchQuestionStat[] | 'unavailable' | null>(null)

  useEffect(() => {
    let cancelled = false
    setHealth(null)
    setError(null)
    void api
      .health()
      .then((r) => !cancelled && setHealth(r))
      .catch((e) => !cancelled && setError(e instanceof Error ? e.message : String(e)))
    void api
      .feedback()
      .then((r) => !cancelled && setReaders(r.pages))
      .catch(() => !cancelled && setReaders('unavailable'))
    void api
      .searchAnalytics()
      .then((r) => !cancelled && setSearches(r.questions))
      .catch(() => !cancelled && setSearches('unavailable'))
    return () => {
      cancelled = true
    }
  }, [])

  if (!health) {
    return (
      <div className="screen">
        <header className="screen__head">
          <h1>Pulse</h1>
        </header>
        {error ? (
          <p className="screen__error">
            <Icon name="alert" size={13} /> {error}
          </p>
        ) : (
          <div className="screen__loading" aria-busy="true">
            <p className="sr-only">Loading…</p>
            <span className="skeleton" />
            <span className="skeleton" />
            <span className="skeleton" />
          </div>
        )}
      </div>
    )
  }

  const { summary, pages, withoutDates } = health

  /**
   * What the published site has reported back.
   *
   * Both halves — the "Was this page helpful?" verdicts and what people
   * searched for — only exist once a site is live and somebody has read it.
   * Before that they were rendered as NOTHING AT ALL, so the screen ended
   * mid-thought and never said why. Empty, they are one surface with one
   * answer, so they get one sentence and one door rather than two silences.
   */
  const readersEmpty = Array.isArray(readers) && readers.length === 0
  const searchesEmpty = Array.isArray(searches) && searches.length === 0
  const noReaderSignals = readersEmpty && searchesEmpty

  return (
    <div className="screen">
      <header className="screen__head screen__head--split">
        <div>
          <h1>Pulse</h1>
          <p className="screen__sub">
            {summary.total} page{summary.total === 1 ? '' : 's'} — health {summary.score}/100.{' '}
            {summary.outdated > 0
              ? `${summary.outdated} outdated, ${summary.review} to review.`
              : summary.review > 0
                ? `${summary.review} to review, nothing outdated.`
                : summary.total > 0
                  ? 'Every page is current.'
                  : 'Nothing to assess yet.'}
          </p>
        </div>
        {/*
          The other half of the picture, from the screen it is read beside.

          Pulse says whether the documentation is still TRUE; Analytics says
          whether anybody is reading it, and nobody looks at one without
          wanting the other. Home used to carry a "Readers" card whose real job
          was this door; retiring that card took the door with it and left
          Analytics reachable from one place, three clicks inside Settings.

          Outlined, not filled: this screen's primary belongs to the work in
          front of somebody, and this is a way to go and look at something else.
        */}
        <button className="btn btn--outline btn--sm" data-see-analytics onClick={onAnalytics}>
          See all the numbers
        </button>
      </header>

      <div className="stats">
        <div className="stat">
          <b>{summary.score}</b>
          <span>health / 100</span>
        </div>
        <div className="stat">
          <b>{summary.current}</b>
          <span>current</span>
        </div>
        <div className="stat">
          <b className={summary.review > 0 ? 'stat--bad' : ''}>{summary.review}</b>
          <span>to review</span>
        </div>
        <div className="stat">
          <b className={summary.outdated > 0 ? 'stat--bad' : ''}>{summary.outdated}</b>
          <span>outdated</span>
        </div>
      </div>

      {withoutDates && summary.total > 0 && (
        <p className="screen__warn">
          <Icon name="info" size={13} /> Page ages are unavailable here, so nothing below is about
          age — only evidence.
        </p>
      )}

      {pages.length === 0 ? (
        <div className="empty">
          <span className="empty__icon">
            <Icon name="pulse" size={20} />
          </span>
          <span className="empty__title">Nothing to assess yet</span>
          <span className="empty__text">Write a page, and Pulse will have something to say.</span>
          <button className="btn btn--tinted btn--sm" onClick={onWrite}>
            <Icon name="page" size={13} />
            Write a page
          </button>
        </div>
      ) : (
        <ul className="pulse-list">
          {pages.map((page) => {
            const changed = ago(page.lastChanged)
            return (
              <li key={page.path} className="pulse-row">
                <button className="pulse-row__main" onClick={() => onOpenPage(page.path)}>
                  <span className={`pulse-mark pulse-mark--${page.state}`} aria-hidden="true">
                    {MARK[page.state]}
                  </span>
                  <span className="pulse-row__body">
                    <span className="pulse-row__title truncate">{titleOf(state, page.path)}</span>
                    <span className="pulse-row__path mono truncate">{page.path}</span>
                  </span>
                  <span className={`pulse-row__state pulse-row__state--${page.state}`}>
                    {LABEL[page.state]}
                  </span>
                  <span className="pulse-row__changed">
                    {changed ? `changed ${changed}` : 'age unknown'}
                  </span>
                  {/* How many people a wrong page was wrong FOR. Not a signal
                      and not part of the state — it is the consequence, and
                      it is why this row is above that one. */}
                  {/* Who to ask. Named on the row because a list of pages
                      that need checking is only actionable if it says who
                      checks them. */}
                  {/*
                    Both of these said what they were in a `title` and nowhere
                    else: a bare word that happens to be the front of an email
                    address, and a number beside an eye. The row is a button,
                    so it HAS an accessible name — it was "Getting started,
                    pages/start.md, Needs review, changed 3 days ago, avery,
                    1,204", which is a list of facts with two of them
                    unlabelled. The words belong in that name, and they cost
                    no space there.

                    The owner keeps a title as well, and only there: it is the
                    full address, which is content the column is too narrow to
                    show, and that is the one thing a tooltip is honestly for.
                  */}
                  {page.owner !== null && (
                    <span className="pulse-row__owner" title={page.owner}>
                      <span className="sr-only">Owner: </span>
                      {page.owner.split('@')[0]}
                    </span>
                  )}
                  {page.readers !== null && (
                    <span className="pulse-row__readers">
                      <Icon name="eye" size={12} />
                      {page.readers.toLocaleString()}
                      <span className="sr-only"> readers in the last 30 days</span>
                    </span>
                  )}
                </button>

                {/*
                  Quiet, not fixed.

                  The row still says the page is overdue and still counts as
                  overdue in the summary — a snooze is a decision about being
                  interrupted, not a claim about the page. What it changes is
                  whether the nightly sweep tells the owner again.
                */}
                {page.nextReview !== null && new Date(page.nextReview) <= new Date() && (
                  <div className="pulse-row__snooze">
                    {page.snoozedUntil !== null && new Date(page.snoozedUntil) > new Date() ? (
                      <>
                        <span className="field__note">
                          Owner not reminded until{' '}
                          {new Date(page.snoozedUntil).toLocaleDateString()}
                        </span>
                        <button
                          className="btn btn--ghost btn--sm"
                          onClick={() => void wake(page.path)}
                        >
                          Remind again
                        </button>
                      </>
                    ) : (
                      <>
                        <span className="field__note">
                          {page.owner === null
                            ? 'Nobody is named for this page, so nobody is being told.'
                            : `${page.owner} is told once, then not again until it is due afresh.`}
                        </span>
                        <button
                          className="btn btn--ghost btn--sm"
                          onClick={() => void snooze(page.path, 7)}
                        >
                          Quiet for a week
                        </button>
                        <button
                          className="btn btn--ghost btn--sm"
                          onClick={() => void snooze(page.path, 30)}
                        >
                          A month
                        </button>
                      </>
                    )}
                  </div>
                )}

                {page.signals.length > 0 && (
                  <ul className="pulse-row__why">
                    {page.signals.map((signal, i) => (
                      <li
                        key={i}
                        className={`pulse-row__signal pulse-row__signal--${signal.weight}`}
                      >
                        <span className="pulse-row__signal-weight">{signal.weight}</span>
                        {signal.detail}
                      </li>
                    ))}
                  </ul>
                )}
              </li>
            )
          })}
        </ul>
      )}

      {pages.length > 0 && (
        <p className="home__quiet">
          Outdated means we can point at something that is now wrong. Needs review means nobody has
          checked recently enough to know. Age alone never makes a page outdated.
        </p>
      )}

      {noReaderSignals && (
        <section className="pulse-fb">
          <h2>What readers say</h2>
          <p className="nothing">
            Nothing from readers yet. Every published page asks &ldquo;Was this page helpful?&rdquo;
            and records what people searched for; both answers land here once somebody reads the
            site.
          </p>
          <div className="home__actions">
            <button className="btn btn--tinted btn--sm" onClick={onWrite}>
              <Icon name="page" size={13} />
              Write a page
            </button>
          </div>
        </section>
      )}

      {/* One side quiet, the other reporting: the screen is NOT empty, so this
          says what is missing and leaves the acting to its neighbour. Two
          buttons for one thought is how a surface stops having one action. */}
      {readersEmpty && !noReaderSignals && (
        <section className="pulse-fb">
          <h2>What readers say</h2>
          <p className="home__quiet">
            No page has been rated yet. The question sits at the foot of every published page.
          </p>
        </section>
      )}

      {Array.isArray(readers) && readers.length > 0 && (
        <section className="pulse-fb">
          <h2>What readers say</h2>
          <p className="home__quiet">
            From the published site&rsquo;s &ldquo;Was this page helpful?&rdquo; — the pages failing
            the most readers first.
          </p>
          <ul className="pulse-list">
            {readers.map((page) => {
              const editorPage = state.pages.find(
                (candidate) => candidate.id === (page.path === '/' ? 'index' : page.path.slice(1)),
              )
              const row = (
                <>
                  <span className="pulse-row__body">
                    <span className="pulse-row__title truncate">
                      {editorPage ? titleOf(state, editorPage.path) : page.path}
                    </span>
                    <span className="pulse-row__path mono truncate">{page.path}</span>
                  </span>
                  <span className="pulse-row__state">
                    <span className="pulse-fb__yes">{page.helpful} helpful</span>
                    {' · '}
                    <span className="pulse-fb__no">{page.notHelpful} not</span>
                  </span>
                </>
              )
              return (
                <li key={page.path} className="pulse-row">
                  {editorPage ? (
                    <button className="pulse-row__main" onClick={() => onOpenPage(editorPage.path)}>
                      {row}
                    </button>
                  ) : (
                    /* The page is not on this draft — renamed, or gone. The
                       verdicts still matter; there is just nothing to open. */
                    <div className="pulse-row__main pulse-fb__still">{row}</div>
                  )}
                  {page.comments.length > 0 && (
                    <ul className="pulse-row__why">
                      {page.comments.map((said, at) => (
                        <li
                          key={at}
                          className={`pulse-row__signal${
                            said.verdict === 'not_helpful' ? ' pulse-row__signal--evidence' : ''
                          }`}
                        >
                          <span className="pulse-row__signal-weight">
                            {said.verdict === 'helpful' ? 'yes' : 'no'}
                          </span>
                          &ldquo;{said.comment}&rdquo;{' '}
                          <span className="pulse-row__changed">{ago(said.createdAt) ?? ''}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </li>
              )
            })}
          </ul>
        </section>
      )}
      {readers === 'unavailable' && (
        <p className="home__quiet">Reader feedback is unavailable right now.</p>
      )}

      {searchesEmpty && !noReaderSignals && (
        <section className="pulse-search">
          <h2>What readers search for</h2>
          <p className="nothing">
            Nobody has searched the site yet. When they do, the queries that found nothing come
            first.
          </p>
        </section>
      )}

      {Array.isArray(searches) && searches.length > 0 && (
        <section className="pulse-search">
          <h2>What readers search for</h2>
          <p className="home__quiet">
            From the published site&rsquo;s search, grouped by the question asked —
            &ldquo;export&rdquo;, &ldquo;how do I export&rdquo; and &ldquo;exports&rdquo; are one
            question. The ones that found nothing come first: each is a page readers wanted and did
            not find.
          </p>
          <ul className="pulse-list">
            {searches.slice(0, 30).map((entry) => (
              <li key={entry.question} className="pulse-row">
                {/* Nothing to open: a query is a question, not a page. The row
                    reuses the feedback rows' non-clickable form. */}
                <div className="pulse-row__main pulse-fb__still">
                  <span className="pulse-row__body">
                    <span className="pulse-row__title truncate">&ldquo;{entry.query}&rdquo;</span>
                    <span className="pulse-row__path mono truncate">
                      searched {entry.searches} time{entry.searches === 1 ? '' : 's'}
                    </span>
                  </span>
                  <span className="pulse-row__state">
                    {entry.isGap ? (
                      <TeachSearch spellings={entry.spellings} branch={branch} />
                    ) : (
                      <>
                        <span className={entry.clicked > 0 ? 'pulse-fb__yes' : ''}>
                          {entry.clicked} clicked
                        </span>
                        {' · '}
                        <span className={entry.abandoned > 0 ? 'pulse-fb__no' : ''}>
                          {entry.abandoned} abandoned
                        </span>
                        {entry.noResults > 0 && (
                          <>
                            {' · '}
                            <span className="pulse-fb__no">{entry.noResults} found nothing</span>
                          </>
                        )}
                      </>
                    )}
                  </span>
                </div>
                {/*
                  The other wordings this row added together.

                  The comment that stood here said they were "named rather
                  than summarised: the count is only believable if somebody
                  can see what was added together" — and then put them in a
                  `title` on a `<span>` inside a row nothing can focus. They
                  were nameable by a mouse and by nothing else, which is the
                  opposite of what the sentence claimed. Shut, so thirty rows
                  stay thirty rows; a press or a tap opens one.
                */}
                {entry.spellings.length > 1 && (
                  <details className="pulse-row__ways">
                    <summary>asked {entry.spellings.length} ways</summary>
                    <ul>
                      {entry.spellings.map((spelling) => (
                        <li key={spelling}>&ldquo;{spelling}&rdquo;</li>
                      ))}
                    </ul>
                  </details>
                )}
              </li>
            ))}
          </ul>
          {searches.length > 30 && (
            <p className="home__quiet">
              Showing the 30 questions that matter most; {searches.length - 30} more behind them.
            </p>
          )}
        </section>
      )}
      {searches === 'unavailable' && (
        <p className="home__quiet">Search analytics are unavailable right now.</p>
      )}
    </div>
  )
}

/**
 * A query that found nothing, and the one thing a writer can do about it.
 *
 * A gap means no page matched, so pinning one would do nothing — a pin only
 * lifts a page that already matched. What fixes a gap is telling search that
 * the reader's word means one of yours: "SSO" for "SAML", "zapier" for
 * "webhooks". That is an ALIAS, and this writes one.
 *
 * It writes to `search.yaml`, which is CONTENT, so it goes on a draft and
 * publishes with everything else. The alternative — a setting that takes
 * effect immediately — would make search behave differently on the live site
 * from the draft that was reviewed, which is the one property drafts exist to
 * prevent.
 */
/**
 * Teach search a word — for every way the question was asked.
 *
 * One alias per spelling, written in one press. Teaching only the leading
 * spelling leaves the reader who typed "how do I export" exactly where they
 * were, which is the failure this whole grouping exists to fix.
 */
function TeachSearch({ spellings, branch }: { spellings: string[]; branch: string | null }) {
  const [open, setOpen] = useState(false)
  const [word, setWord] = useState('')
  const [state, setState] = useState<'idle' | 'saving' | 'saved' | 'failed'>('idle')
  const [problem, setProblem] = useState<string | null>(null)

  if (!branch) {
    // Read-only on the live site, the same rule the Variables screen follows:
    // content changes happen on a draft.
    return <span className="pulse-search__gap">found nothing</span>
  }

  if (state === 'saved') {
    return (
      <span className="pulse-search__taught">
        <Icon name="check" size={12} /> search now knows
      </span>
    )
  }

  const teach = async () => {
    const target = word.trim()
    if (!target) return
    setState('saving')
    setProblem(null)
    try {
      // Read, change, write. The whole file, because the alternative is
      // editing YAML in place and a parser that round-trips trivia.
      const current = await api.page('search.yaml', branch).catch(() => null)
      const tuning = parseSearchTuning(current?.content ?? '')
      for (const spelling of spellings)
        tuning.aliases[spelling.toLowerCase()] = target.toLowerCase()
      // The file being replaced, so the notes in it survive: this file is where
      // somebody writes WHY a correction exists ("pinned for the launch,
      // remove in October"), and the whole-file write above would drop them.
      await api.write(
        branch,
        'search.yaml',
        serializeSearchTuning(tuning, current?.content),
        current?.revision,
      )
      setState('saved')
    } catch (e) {
      setState('failed')
      setProblem(e instanceof Error ? e.message : String(e))
    }
  }

  if (!open) {
    return (
      <span className="pulse-search__gap">
        found nothing
        <button className="btn btn--tinted btn--sm" onClick={() => setOpen(true)}>
          Teach search
        </button>
      </span>
    )
  }

  return (
    <span className="pulse-search__teach">
      <label>
        our docs call it
        <input
          className="input"
          value={word}
          autoFocus
          placeholder="webhooks"
          onChange={(e) => setWord(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && void teach()}
        />
      </label>
      <button
        className="btn btn--primary btn--sm"
        disabled={word.trim() === '' || state === 'saving'}
        onClick={() => void teach()}
      >
        {state === 'saving' ? <Icon name="spinner" size={12} /> : null}
        Save
      </button>
      {problem && <span className="pulse-fb__no">{problem}</span>}
    </span>
  )
}
